import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Product, Distributor, ProductVariant } from '../../types/index.js';
import { Plus, Trash2, ShoppingCart, Truck } from 'lucide-react';
import { api } from '../../services/api.js';
import { computePromoSplit } from '../../utils/promotion';
import { ProductVariantSelects } from '../orders/ProductVariantSelects';
import {
  defaultOrderDistributorId,
  sortDistributorsForOrderCapture,
} from '../../utils/orderDistributorSort';
import { catalogCasePrice, isSupermarketStoreType, lehUnitPrice } from '../../utils/casePrice';

export interface OrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  promotion_type: '1+1' | '2+1' | '3+1' | '4+1' | 'free_samples' | 'none';
  distributor_id?: string;
  distributor_name?: string;
  variant_id?: string;
  variant_name?: string;
  unit_price_override?: number;
}

interface OrderCollectionFormProps {
  products: Product[];
  distributors: Distributor[];
  value?: OrderItem[];
  onChange: (orders: OrderItem[]) => void;
  /** PoS store type. Supermarket chains show the LEH case price. */
  storeType?: string | null;
}

type PromotionType = OrderItem['promotion_type'];

interface NewOrderState {
  product_id: string;
  variant_id: string;
  quantity: number | '';
  promotion_type: PromotionType;
  distributor_id: string;
  unit_price_override: string;
}

const initialNewOrder = (distributorId = ''): NewOrderState => ({
  product_id: '',
  variant_id: '',
  quantity: 1,
  promotion_type: 'none',
  distributor_id: distributorId,
  unit_price_override: '',
});

export const OrderCollectionForm: React.FC<OrderCollectionFormProps> = ({
  products,
  distributors,
  value = [],
  onChange,
  storeType = null,
}) => {
  const sortedDistributors = sortDistributorsForOrderCapture(distributors);
  const defaultDistributorId = defaultOrderDistributorId(sortedDistributors);
  const [orders, setOrders] = useState<OrderItem[]>(value);
  const [newOrder, setNewOrder] = useState<NewOrderState>(
    initialNewOrder(defaultDistributorId)
  );
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  useEffect(() => {
    if (!defaultDistributorId) return;
    setNewOrder((prev) =>
      prev.distributor_id ? prev : initialNewOrder(defaultDistributorId)
    );
  }, [defaultDistributorId]);

  useEffect(() => {
    if (!newOrder.product_id) {
      setVariants([]);
      return;
    }
    let active = true;
    api
      .get(`/products/${newOrder.product_id}/variants`)
      .then((res) => {
        if (!active) return;
        const list: ProductVariant[] = res.data || [];
        setVariants(list);
        setNewOrder((prev) => ({
          ...prev,
          variant_id: list.length === 1 ? list[0].id : '',
        }));
      })
      .catch(() => {
        if (!active) return;
        setVariants([]);
        setNewOrder((prev) => ({ ...prev, variant_id: '' }));
        toast.error('Failed to load product variants');
      });
    return () => {
      active = false;
    };
  }, [newOrder.product_id]);

  const selectedDistributor = distributors.find(
    (d) => d.id === newOrder.distributor_id
  );
  const isSelfDelivery = selectedDistributor?.is_self === true;
  const selectedVariant = variants.find((v) => v.id === newOrder.variant_id);
  const selectedCasePrice = selectedVariant
    ? catalogCasePrice(selectedVariant, storeType)
    : null;
  const selectedLehUnit = selectedVariant
    ? lehUnitPrice(selectedVariant.leh_unit_price_net)
    : null;
  const showLehHint = isSupermarketStoreType(storeType)
    && selectedLehUnit !== null
    && selectedCasePrice !== null;

  const handleAddOrder = () => {
    const qty =
      typeof newOrder.quantity === 'number' ? newOrder.quantity : NaN;
    if (!newOrder.product_id || !Number.isFinite(qty) || qty < 1) {
      toast.warning('Please select a product and a quantity of at least 1');
      return;
    }

    const product = products.find((p) => p.id === newOrder.product_id);
    if (!product) return;

    if (variants.length > 0 && !newOrder.variant_id) {
      toast.warning('Please select a Gebinde variant');
      return;
    }

    if (!newOrder.distributor_id) {
      toast.warning('Please select a distributor');
      return;
    }

    const dist = distributors.find((d) => d.id === newOrder.distributor_id);
    const variant = variants.find((v) => v.id === newOrder.variant_id);
    const override =
      newOrder.unit_price_override.trim() !== ''
        ? Number(newOrder.unit_price_override)
        : undefined;

    const orderItem: OrderItem = {
      product_id: newOrder.product_id,
      product_name: product.name,
      quantity: qty,
      promotion_type: newOrder.promotion_type,
      distributor_id: newOrder.distributor_id || undefined,
      distributor_name: dist?.name,
      variant_id: variant?.id,
      variant_name: variant?.gebinde_name,
      unit_price_override:
        override !== undefined && Number.isFinite(override) && override > 0
          ? override
          : undefined,
    };

    const updated = [...orders, orderItem];
    setOrders(updated);
    onChange(updated);

    setNewOrder(initialNewOrder(newOrder.distributor_id));
    setVariants([]);
  };

  const handleRemoveOrder = (index: number) => {
    const updated = orders.filter((_, i) => i !== index);
    setOrders(updated);
    onChange(updated);
  };

  const getPromotionLabel = (promo: string) => {
    if (promo === 'none') return 'No Promo';
    if (promo === 'free_samples') return 'Free Samples';
    return `${promo}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingCart size={20} className="text-blue-600" />
        <h3 className="text-lg font-bold text-gray-900">Order lines</h3>
      </div>

      {orders.length > 0 && (
        <div className="space-y-2 mb-4">
          {orders.map((order, index) => {
            const split = computePromoSplit(order.quantity, order.promotion_type);
            const showSplit = split.free > 0;
            return (
              <div
                key={index}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {order.product_name}
                    {order.variant_name && (
                      <span className="ml-1 font-normal text-gray-500">
                        · {order.variant_name}
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
                    <span>
                      Qty: <strong>{order.quantity}</strong>
                      {showSplit && (
                        <span className="ml-1 text-yellow-700">
                          (deliver {split.delivered} = {split.paid} + {split.free} free)
                        </span>
                      )}
                    </span>
                    <span>
                      Promo: <strong>{getPromotionLabel(order.promotion_type)}</strong>
                    </span>
                    {order.unit_price_override !== undefined && (
                      <span>
                        Price:{' '}
                        <strong>{order.unit_price_override.toFixed(2)} EUR</strong>
                      </span>
                    )}
                    {order.distributor_name && (
                      <span className="flex items-center gap-1">
                        <Truck size={14} />
                        <strong>{order.distributor_name}</strong>
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveOrder(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50 space-y-3">
        <h4 className="font-semibold text-gray-900">Add Order</h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ProductVariantSelects
            products={products}
            variants={variants}
            productId={newOrder.product_id}
            variantId={newOrder.variant_id}
            onProductChange={(productId) =>
              setNewOrder({ ...newOrder, product_id: productId, variant_id: '' })
            }
            onVariantChange={(variantId) =>
              setNewOrder({ ...newOrder, variant_id: variantId })
            }
            formatVariantLabel={(v) => {
              const price = catalogCasePrice(v, storeType).toFixed(2);
              const volume = v.unit_volume || '';
              return `${v.gebinde_name} (${v.units_per_case}× ${volume}) - ${price} EUR`;
            }}
            controlClass="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              inputMode="numeric"
              value={newOrder.quantity}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const raw = e.target.value;
                setNewOrder({
                  ...newOrder,
                  quantity: raw === '' ? '' : parseInt(raw, 10),
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price override (EUR/case)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder={
                selectedCasePrice !== null ? selectedCasePrice.toFixed(2) : '0.00'
              }
              value={newOrder.unit_price_override}
              onFocus={(e) => e.target.select()}
              onChange={(e) =>
                setNewOrder({ ...newOrder, unit_price_override: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            {showLehHint && selectedLehUnit !== null && selectedCasePrice !== null && (
              <p className="text-xs text-gray-500 mt-1">
                LEH {selectedLehUnit.toFixed(2)} EUR/unit · {selectedCasePrice.toFixed(2)} EUR/case
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Promotion
            </label>
            <select
              value={newOrder.promotion_type}
              onChange={(e) =>
                setNewOrder({
                  ...newOrder,
                  promotion_type: e.target.value as PromotionType,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="none">No Promo</option>
              <option value="1+1">1+1 Free</option>
              <option value="2+1">2+1 Free</option>
              <option value="3+1">3+1 Free</option>
              <option value="4+1">4+1 Free</option>
              <option value="free_samples">Free Samples</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Distributor
            </label>
            <select
              value={newOrder.distributor_id}
              onChange={(e) =>
                setNewOrder({ ...newOrder, distributor_id: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              {sortedDistributors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.is_self ? ' (Own Delivery)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isSelfDelivery && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Truck size={16} />
            <span className="font-medium">
              This order will enter your delivery backlog
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={handleAddOrder}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
        >
          <Plus size={18} />
          Add Order
        </button>
      </div>

      {orders.length === 0 && (
        <p className="text-center py-4 text-gray-500 italic text-sm">
          No order lines recorded yet.
        </p>
      )}
    </div>
  );
};
